import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const inputSt = {
  width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 8,
  padding: '10px 12px', fontSize: 13, boxSizing: 'border-box',
  fontFamily: T.font, color: T.ink, background: 'white', outline: 'none',
};

const STATUS_CFG = {
  entwurf:       { label: 'Entwurf',      bg:'#f3f4f6', color:'#888' },
  in_bearbeitung:{ label: 'In Bearbeitung',bg:'#fef3c7', color:'#92400e' },
  abgeschlossen: { label: 'Abgeschlossen', bg:'#dcfce7', color:'#15803d' },
  verschickt:    { label: 'Verschickt',    bg:'#dcfce7', color:'#15803d' },
};

function fmtDate(d) {
  if (!d) return '';
  const [y,m,day] = String(d).split('-');
  return `${day}.${m}.${y}`;
}
function isoFromDE(de) {
  if (!de) return '';
  const [d,m,y] = de.split('.');
  return `${y}-${m}-${d}`;
}
function currentYear() { return new Date().getFullYear(); }

function yearRange(y) {
  return { label: `01.01.${y} – 31.12.${y}`, start: `${y}-01-01`, end: `${y}-12-31` };
}
const YEAR_OPTIONS = [0,1,2,3].map(i => yearRange(currentYear() - i));

/* ══════════════════════════════════════════════
   Step 1: Property Select Modal
══════════════════════════════════════════════ */
function PropertySelectModal({ properties, onSelect, onClose }) {
  const [q, setQ] = useState('');
  const filtered = properties.filter(p =>
    p.name?.toLowerCase().includes(q.toLowerCase()) ||
    [p.street, p.house_number, p.zip, p.city].filter(Boolean).join(' ').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position:'fixed',inset:0,zIndex:200,background:'rgba(17,24,39,0.45)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div style={{ background:'white',borderRadius:16,width:'100%',maxWidth:640,maxHeight:'80vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(0,0,0,0.16)' }}>
        <div style={{ padding:'22px 28px 18px',borderBottom:`1px solid ${T.border}` }}>
          <h2 style={{ margin:'0 0 16px',fontSize:18,fontWeight:800,color:T.ink,textAlign:'center' }}>Nebenkostenabrechnung erstellen</h2>
          <div style={{ fontSize:14,fontWeight:700,color:T.ink,marginBottom:12 }}>Wähle ein Objekt</div>
          <div style={{ position:'relative' }}>
            <svg style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Objekte durchsuchen..." style={{ ...inputSt, paddingLeft:36 }} autoFocus />
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'8px 0' }}>
          {filtered.length === 0 ? (
            <div style={{ padding:'32px',textAlign:'center',color:T.muted,fontSize:13 }}>Keine Objekte gefunden</div>
          ) : filtered.map(p => (
            <div key={p.id} onClick={() => onSelect(p)}
              style={{ display:'flex',alignItems:'center',gap:14,padding:'14px 28px',cursor:'pointer',transition:'background 0.1s',borderBottom:`1px solid ${T.border}` }}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg}
              onMouseLeave={e=>e.currentTarget.style.background='white'}>
              <div style={{ width:40,height:40,borderRadius:10,background:'#e0f2fe',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="8" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="16" y2="21"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="2" y1="15" x2="22" y2="15"/></svg>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14,fontWeight:700,color:T.ink }}>{p.name}</div>
                <div style={{ fontSize:12,color:T.muted,marginTop:2 }}>
                  {[p.street, p.house_number].filter(Boolean).join(' ')}{p.zip||p.city ? `, ${[p.zip,p.city].filter(Boolean).join(' ')}` : ''}
                </div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          ))}
        </div>
        <div style={{ padding:'16px 28px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 20px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:14,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Abbrechen</button>
          <button disabled style={{ padding:'9px 20px',borderRadius:9,border:'none',background:'#d1d5db',color:'white',fontSize:14,cursor:'not-allowed',fontFamily:T.font,fontWeight:600 }}>Abrechnung erstellen</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Step 2a: BKA aus Vorjahr — show previous
══════════════════════════════════════════════ */
function BkaVorjahrModal({ property, prevBkas, onNewInstead, onClose }) {
  const PropHeader = () => (
    <div style={{ display:'flex',alignItems:'center',gap:14,padding:'0 0 16px',borderBottom:`1px solid ${T.border}`,marginBottom:20 }}>
      <div style={{ width:40,height:40,borderRadius:10,background:'#e0f2fe',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="8" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="16" y2="21"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="2" y1="15" x2="22" y2="15"/></svg>
      </div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:14,fontWeight:700,color:T.ink }}>{property.name}</div>
        <div style={{ fontSize:12,color:T.muted }}>{[property.street, property.house_number].filter(Boolean).join(' ')}{property.zip||property.city ? `, ${[property.zip,property.city].filter(Boolean).join(' ')}` : ''}</div>
      </div>
      <button onClick={onNewInstead} style={{ fontSize:13,color:T.ink,background:'none',border:'none',cursor:'pointer',fontWeight:700,fontFamily:T.font }}>Ändern</button>
    </div>
  );

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position:'fixed',inset:0,zIndex:200,background:'rgba(17,24,39,0.45)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div style={{ background:'white',borderRadius:16,width:'100%',maxWidth:640,boxShadow:'0 24px 64px rgba(0,0,0,0.16)' }}>
        <div style={{ padding:'22px 28px 20px',borderBottom:`1px solid ${T.border}`,textAlign:'center' }}>
          <h2 style={{ margin:0,fontSize:18,fontWeight:800,color:T.ink }}>Nebenkostenabrechnung erstellen</h2>
        </div>
        <div style={{ padding:'24px 28px' }}>
          <PropHeader />
          {prevBkas.length === 0 ? (
            <div style={{ textAlign:'center',padding:'32px 0' }}>
              <div style={{ marginBottom:16 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display:'block',margin:'0 auto' }}>
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div style={{ fontSize:17,fontWeight:800,color:T.ink,marginBottom:10 }}>Hier gibt es keine Abrechnungen</div>
              <div style={{ fontSize:13,color:T.muted,maxWidth:340,margin:'0 auto 24px',lineHeight:1.65 }}>
                In dem ausgewählten Objekt gibt es noch keine abgeschlossenen Abrechnungen, erstell doch eine neue Abrechnung.
              </div>
              <button onClick={onNewInstead} style={{ padding:'11px 28px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>
                Neue Abrechnung erstellen
              </button>
            </div>
          ) : (
            <div>
              {prevBkas.map(b => (
                <div key={b.id} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',borderBottom:`1px solid ${T.border}` }}>
                  <div>
                    <div style={{ fontSize:13,fontWeight:700,color:T.ink }}>{b.title}</div>
                    <div style={{ fontSize:12,color:T.muted }}>{fmtDate(b.periode_start)} – {fmtDate(b.periode_end)}</div>
                  </div>
                  <span style={{ fontSize:11,padding:'3px 10px',borderRadius:20,...(STATUS_CFG[b.status]||STATUS_CFG.entwurf) }}>{(STATUS_CFG[b.status]||STATUS_CFG.entwurf).label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding:'16px 28px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 20px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:14,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Abbrechen</button>
          <button disabled style={{ padding:'9px 20px',borderRadius:9,border:'none',background:'#d1d5db',color:'white',fontSize:14,cursor:'not-allowed',fontFamily:T.font,fontWeight:600 }}>Abrechnung erstellen</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Step 2b: Period select
══════════════════════════════════════════════ */
function PeriodSelectModal({ property, onStart, onBack, onClose }) {
  const { isMobile } = useBreakpoint();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected]         = useState(YEAR_OPTIONS[0]);
  const [isCustom, setIsCustom]         = useState(false);
  const [customStart, setCustomStart]   = useState(`01.01.${currentYear()}`);
  const [customEnd, setCustomEnd]       = useState(`31.12.${currentYear()}`);

  function pickYear(opt) { setSelected(opt); setIsCustom(false); setShowDropdown(false); }
  function pickCustom()  { setIsCustom(true); setShowDropdown(false); }

  const displayLabel = isCustom ? 'Individueller Zeitraum' : selected.label;
  const startIso = isCustom ? isoFromDE(customStart) : selected.start;
  const endIso   = isCustom ? isoFromDE(customEnd)   : selected.end;
  const ready = startIso && endIso;

  return (
    <div onClick={e => { if (e.target === e.currentTarget) { setShowDropdown(false); onClose(); } }} style={{ position:'fixed',inset:0,zIndex:200,background:'rgba(17,24,39,0.45)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div style={{ background:'white',borderRadius:16,width:'100%',maxWidth:640,boxShadow:'0 24px 64px rgba(0,0,0,0.16)' }}>
        <div style={{ padding:'22px 28px 20px',borderBottom:`1px solid ${T.border}`,textAlign:'center' }}>
          <h2 style={{ margin:0,fontSize:18,fontWeight:800,color:T.ink }}>Nebenkostenabrechnung erstellen</h2>
        </div>
        <div style={{ padding:'24px 28px' }}>
          {/* Property header */}
          <div style={{ display:'flex',alignItems:'center',gap:14,padding:'0 0 16px',borderBottom:`1px solid ${T.border}`,marginBottom:20 }}>
            <div style={{ width:40,height:40,borderRadius:10,background:'#e0f2fe',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="8" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="16" y2="21"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="2" y1="15" x2="22" y2="15"/></svg>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14,fontWeight:700,color:T.ink }}>{property.name}</div>
              <div style={{ fontSize:12,color:T.muted }}>{[property.street,property.house_number].filter(Boolean).join(' ')}{property.zip||property.city?`, ${[property.zip,property.city].filter(Boolean).join(' ')}`:''}</div>
            </div>
            <button onClick={onBack} style={{ fontSize:13,color:T.ink,background:'none',border:'none',cursor:'pointer',fontWeight:700,fontFamily:T.font }}>Ändern</button>
          </div>

          <div style={{ fontSize:15,fontWeight:800,color:T.ink,marginBottom:16 }}>Gib den Abrechnungszeitraum an</div>

          {/* Period dropdown */}
          <div style={{ marginBottom: isCustom ? 16 : 0 }}>
            <label style={{ display:'block',fontSize:12,fontWeight:600,color:T.muted,marginBottom:6 }}>Zeitraum</label>
            <div style={{ position:'relative' }}>
              <button onClick={() => setShowDropdown(v => !v)} style={{ ...inputSt, textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', background:'white' }}>
                <span>{displayLabel}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {showDropdown && (
                <div style={{ position:'absolute',top:'100%',left:0,right:0,zIndex:50,background:'white',border:`1.5px solid ${T.border}`,borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',marginTop:4,overflow:'hidden' }}>
                  {YEAR_OPTIONS.map(opt => (
                    <div key={opt.label} onClick={() => pickYear(opt)} style={{ padding:'12px 16px',fontSize:13,cursor:'pointer',color:T.ink,borderBottom:`1px solid ${T.border}`,transition:'background 0.1s' }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.bg} onMouseLeave={e=>e.currentTarget.style.background='white'}>
                      {opt.label}
                    </div>
                  ))}
                  <div onClick={pickCustom} style={{ padding:'12px 16px',fontSize:13,cursor:'pointer',color:T.ink,transition:'background 0.1s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=T.bg} onMouseLeave={e=>e.currentTarget.style.background='white'}>
                    Individueller Zeitraum
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom date inputs */}
          {isCustom && (
            <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',gap:14,marginTop:16 }}>
              <div>
                <label style={{ display:'block',fontSize:12,fontWeight:600,color:T.muted,marginBottom:6 }}>Startdatum</label>
                <div style={{ position:'relative' }}>
                  <input value={customStart} onChange={e=>setCustomStart(e.target.value)} placeholder="TT.MM.JJJJ" style={inputSt}
                    onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
                  <svg style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
              </div>
              <div>
                <label style={{ display:'block',fontSize:12,fontWeight:600,color:T.muted,marginBottom:6 }}>Enddatum</label>
                <div style={{ position:'relative' }}>
                  <input value={customEnd} onChange={e=>setCustomEnd(e.target.value)} placeholder="TT.MM.JJJJ" style={inputSt}
                    onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
                  <svg style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
              </div>
            </div>
          )}
        </div>
        <div style={{ padding:'16px 28px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 20px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:14,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Abbrechen</button>
          <button onClick={() => ready && onStart(startIso, endIso)} disabled={!ready}
            style={{ padding:'9px 24px',borderRadius:9,border:'none',background:ready?T.ink:'#d1d5db',color:'white',fontSize:14,fontWeight:700,cursor:ready?'pointer':'not-allowed',fontFamily:T.font }}>
            Abrechnung erstellen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   BKA Wizard — full-screen 4-step flow
══════════════════════════════════════════════ */
const WIZARD_STEPS = [
  { key:'anlage',     label:'Anlage',                  sub: null },
  { key:'kosten_obj', label:'Kostenaufstellung &\nVerteilung', sub:'des Objekts' },
  { key:'kosten_ein', label:'Kostenaufstellung &\nVerteilung', sub:'der Einheiten' },
  { key:'abschluss',  label:'Prüfung & Abschluss',     sub: null },
];

function BKAWizard({ property, periodeStart, periodeEnd, onSave, onCancel }) {
  const { isMobile } = useBreakpoint();
  const [step, setStep]    = useState(0);
  const [title, setTitle]  = useState('Nebenkostenabrechnung');
  const [alleME, setAlleME]   = useState(true);
  const [alleVZ, setAlleVZ]   = useState(true);
  const [saving, setSaving]   = useState(false);

  async function handleFinish() {
    setSaving(true);
    await onSave({ title, periode_start: periodeStart, periode_end: periodeEnd, alle_mieteinheiten: alleME, alle_vorauszahlungen: alleVZ });
    setSaving(false);
  }

  const Toggle = ({ val, onChange, label, hint }) => (
    <div style={{ display:'flex',alignItems:'flex-start',gap:14 }}>
      <div onClick={() => onChange(!val)} style={{ width:44,height:26,borderRadius:13,background:val?T.ink:'#d1d5db',cursor:'pointer',position:'relative',transition:'background 0.2s',flexShrink:0,marginTop:2 }}>
        <div style={{ position:'absolute',top:3,left:val?19:3,width:20,height:20,borderRadius:'50%',background:'white',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.25)' }} />
      </div>
      <div>
        <span style={{ fontSize:14,color:T.ink,fontWeight:500 }}>{label}</span>
        {hint && <span style={{ fontSize:12,color:T.muted,marginLeft:6,background:'#f3f4f6',borderRadius:'50%',display:'inline-flex',width:16,height:16,alignItems:'center',justifyContent:'center',cursor:'help' }}>?</span>}
      </div>
    </div>
  );

  return (
    <div style={{ position:'fixed',inset:0,zIndex:300,background:'white',display:'flex',flexDirection:'column',fontFamily:T.font }}>
      {/* Progress stepper */}
      <div style={{ borderBottom:`1px solid ${T.border}`,padding:'0 40px',background:'white' }}>
        <div style={{ display:'flex',alignItems:'flex-start',gap:0,maxWidth:900,margin:'0 auto' }}>
          {WIZARD_STEPS.map((s,i) => {
            const done    = i < step;
            const active  = i === step;
            const color   = active ? T.ink : done ? T.ink : '#d1d5db';
            return (
              <div key={s.key} style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',position:'relative',paddingTop:20,paddingBottom:12 }}>
                {i > 0 && <div style={{ position:'absolute',left:0,top:30,width:'50%',height:2,background:done||active?T.ink:'#e5e5e5',zIndex:0 }} />}
                {i < WIZARD_STEPS.length-1 && <div style={{ position:'absolute',right:0,top:30,width:'50%',height:2,background:done?T.ink:'#e5e5e5',zIndex:0 }} />}
                <div style={{ width:20,height:20,borderRadius:'50%',background:active||done?T.ink:'#e5e5e5',border:`2px solid ${active||done?T.ink:'#e5e5e5'}`,display:'flex',alignItems:'center',justifyContent:'center',zIndex:1,marginBottom:8,transition:'background 0.2s' }}>
                  {done ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                       : <div style={{ width:8,height:8,borderRadius:'50%',background:active?'white':'#9ca3af' }} />}
                </div>
                <div style={{ fontSize:11,fontWeight:600,color,textAlign:'center',lineHeight:1.3,whiteSpace:'pre-line' }}>{s.label}</div>
                {s.sub && <div style={{ fontSize:10,color:active?T.ink:T.muted,textAlign:'center' }}>{s.sub}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex:1, overflowY:'auto', padding:'40px' }}>
        <div style={{ maxWidth:900,margin:'0 auto' }}>
          <h2 style={{ fontSize:22,fontWeight:800,color:T.ink,marginBottom:32,letterSpacing:'-0.5px' }}>
            Nebenkostenabrechnung: {property.name}
          </h2>

          {step === 0 && (
            <>
              <div style={{ fontSize:15,fontWeight:700,color:T.ink,marginBottom:20 }}>Lege den Titel und den Abrechnungszeitraum fest.</div>
              <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',gap:20,marginBottom:36 }}>
                <div>
                  <label style={{ display:'block',fontSize:12,fontWeight:600,color:T.muted,marginBottom:6 }}>Titel der Abrechnung</label>
                  <input value={title} onChange={e=>setTitle(e.target.value)} style={inputSt}
                    onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
                </div>
                <div>
                  <label style={{ display:'block',fontSize:12,fontWeight:600,color:T.muted,marginBottom:6 }}>Abrechnungszeitraum</label>
                  <div style={{ display:'flex',gap:8 }}>
                    <div style={{ position:'relative',flex:1 }}>
                      <input readOnly value={fmtDate(periodeStart)} style={{ ...inputSt,color:T.muted }} />
                      <svg style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    </div>
                    <div style={{ position:'relative',flex:1 }}>
                      <input readOnly value={fmtDate(periodeEnd)} style={{ ...inputSt,color:T.muted }} />
                      <svg style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ fontSize:15,fontWeight:700,color:T.ink,marginBottom:20 }}>Auswahl der betroffenen Mieteinheiten und Vorauszahlungen</div>
              <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',gap:24 }}>
                <Toggle val={alleME} onChange={setAlleME} label="Berücksichtigung aller Mieteinheiten" hint />
                <Toggle val={alleVZ} onChange={setAlleVZ} label="Berücksichtigung aller geleisteten Vorauszahlungen für den Abrechnungszeitraum" hint />
              </div>
            </>
          )}

          {step === 1 && (
            <div style={{ textAlign:'center',padding:'60px 32px' }}>
              <div style={{ width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px',border:'1.5px solid e5e5e5' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
              </div>
              <div style={{ fontSize:17,fontWeight:700,color:T.ink,marginBottom:8 }}>Kostenaufstellung & Verteilung des Objekts</div>
              <div style={{ fontSize:13,color:T.muted,maxWidth:400,margin:'0 auto' }}>Hier werden die Betriebskosten des Objekts erfasst und auf die Mieter verteilt.</div>
            </div>
          )}
          {step === 2 && (
            <div style={{ textAlign:'center',padding:'60px 32px' }}>
              <div style={{ width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px',border:'1.5px solid e5e5e5' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              <div style={{ fontSize:17,fontWeight:700,color:T.ink,marginBottom:8 }}>Kostenaufstellung & Verteilung der Einheiten</div>
              <div style={{ fontSize:13,color:T.muted,maxWidth:400,margin:'0 auto' }}>Die Kosten werden anteilig auf die einzelnen Mieteinheiten verteilt.</div>
            </div>
          )}
          {step === 3 && (
            <div style={{ textAlign:'center',padding:'60px 32px' }}>
              <div style={{ width:64,height:64,background:'linear-gradient(135deg,#f0fdf4,#dcfce7)',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px',border:'1.5px solid #bbf7d0' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div style={{ fontSize:17,fontWeight:700,color:T.ink,marginBottom:8 }}>Prüfung & Abschluss</div>
              <div style={{ fontSize:13,color:T.muted,maxWidth:400,margin:'0 auto',marginBottom:24 }}>Abrechnung wird als Entwurf gespeichert. Du kannst sie jederzeit weiter bearbeiten.</div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop:`1px solid ${T.border}`,padding:'16px 40px',display:'flex',justifyContent:'space-between',alignItems:'center',background:'white' }}>
        <button onClick={onCancel} style={{ fontSize:14,color:T.muted,background:'none',border:'none',cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Abbrechen</button>
        <div style={{ display:'flex',gap:10 }}>
          {step > 0 && <button onClick={() => setStep(s=>s-1)} style={{ padding:'10px 20px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:14,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Zurück</button>}
          {step < WIZARD_STEPS.length-1
            ? <button onClick={() => setStep(s=>s+1)} style={{ padding:'10px 28px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>Weiter</button>
            : <button onClick={handleFinish} disabled={saving} style={{ padding:'10px 28px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:T.font,opacity:saving?0.7:1 }}>{saving?'Wird gespeichert…':'Fertigstellen'}</button>
          }
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Nebenkosten Page
══════════════════════════════════════════════ */
export default function Nebenkosten() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [abrechnungen, setAbrechnungen] = useState([]);
  const [properties, setProperties]     = useState([]);
  const [loading, setLoading]           = useState(true);

  /* modal flow state */
  const [flow, setFlow]           = useState(null);  // 'neue' | 'vorjahr'
  const [modalStep, setModalStep] = useState(null);  // 'property' | 'vorjahr' | 'period'
  const [selProp, setSelProp]     = useState(null);
  const [prevBkas, setPrevBkas]   = useState([]);

  /* wizard */
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardProp, setWizardProp] = useState(null);
  const [wizardStart, setWizardStart] = useState(null);
  const [wizardEnd, setWizardEnd]     = useState(null);

  /* filters */
  const [search, setSearch]       = useState('');
  const [filterProp, setFilterProp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const [{ data:a }, { data:p }] = await Promise.all([
      supabase.from('nebenkosten_abrechnungen').select('*').eq('owner_id', user.id).order('created_at', { ascending:false }),
      supabase.from('properties').select('id,name,street,house_number,zip,city').eq('owner_id', user.id).order('name'),
    ]);
    setAbrechnungen(a || []);
    setProperties(p || []);
    setLoading(false);
  }

  /* open flows */
  function openNeue()    { setFlow('neue');    setModalStep('property'); setSelProp(null); }
  function openVorjahr() { setFlow('vorjahr'); setModalStep('property'); setSelProp(null); }

  async function handlePropSelect(prop) {
    setSelProp(prop);
    if (flow === 'vorjahr') {
      const { data } = await supabase.from('nebenkosten_abrechnungen').select('*').eq('owner_id', user.id).eq('property_id', prop.id).eq('status', 'abgeschlossen').order('periode_start', { ascending:false });
      setPrevBkas(data || []);
      setModalStep('vorjahr');
    } else {
      setModalStep('period');
    }
  }

  function handleStartWizard(start, end) {
    setWizardProp(selProp);
    setWizardStart(start);
    setWizardEnd(end);
    setModalStep(null);
    setWizardOpen(true);
  }

  async function handleWizardSave(formData) {
    const { error } = await supabase.from('nebenkosten_abrechnungen').insert({
      ...formData,
      property_id: wizardProp?.id || null,
      owner_id: user.id,
    });
    if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    setWizardOpen(false);
    setWizardProp(null);
    await fetchAll();
    addToast('Abrechnung als Entwurf gespeichert.', 'success');
  }

  const hasFilter = search || filterProp || filterStatus;
  const filtered  = abrechnungen.filter(a => {
    const q = search.toLowerCase();
    const matchQ = !q || a.title?.toLowerCase().includes(q);
    const matchP = !filterProp || a.property_id === filterProp;
    const matchS = !filterStatus || a.status === filterStatus;
    return matchQ && matchP && matchS;
  });

  const propName = id => properties.find(p => p.id === id)?.name || '–';

  if (wizardOpen) {
    return <BKAWizard property={wizardProp || {}} periodeStart={wizardStart} periodeEnd={wizardEnd} onSave={handleWizardSave} onCancel={() => setWizardOpen(false)} />;
  }

  return (
    <div style={{ maxWidth:1200, color:T.ink, fontFamily:T.font }}>

      {/* Modals */}
      {modalStep === 'property' && (
        <PropertySelectModal
          properties={properties}
          onSelect={handlePropSelect}
          onClose={() => setModalStep(null)}
        />
      )}
      {modalStep === 'vorjahr' && selProp && (
        <BkaVorjahrModal
          property={selProp}
          prevBkas={prevBkas}
          onNewInstead={() => setModalStep('period')}
          onClose={() => setModalStep(null)}
        />
      )}
      {modalStep === 'period' && selProp && (
        <PeriodSelectModal
          property={selProp}
          onStart={handleStartWizard}
          onBack={() => setModalStep('property')}
          onClose={() => setModalStep(null)}
        />
      )}

      {/* ── Header ── */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:28,gap:12,flexWrap:'wrap' }}>
        <h1 style={{ margin:0,fontSize:isMobile?22:28,fontWeight:800,letterSpacing:'-0.6px',color:T.ink }}>Betriebskostenabrechnungen</h1>
        <div style={{ display:'flex',gap:10 }}>
          <button onClick={openVorjahr} style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:13,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            BKA aus Vorjahr erstellen
          </button>
          <button onClick={openNeue} style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:13,cursor:'pointer',fontFamily:T.font,fontWeight:700 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Neue Abrechnung erstellen
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:20,flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:'1 1 200px',minWidth:160 }}>
          <svg style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="text" placeholder="Nebenkosten durchsuchen..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%',border:`1.5px solid ${T.border}`,borderRadius:9,padding:'8px 12px 8px 32px',fontSize:13,boxSizing:'border-box',outline:'none',fontFamily:T.font,height:38 }} />
        </div>

        <button style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',border:`1.5px solid ${T.border}`,borderRadius:9,background:'white',fontSize:13,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600,height:38 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
          Filter
        </button>

        {/* Objekte chip */}
        <div style={{ position:'relative' }}>
          <select value={filterProp} onChange={e=>setFilterProp(e.target.value)}
            style={{ appearance:'none',WebkitAppearance:'none',background:filterProp?'#f5f5f5':'white',border:`1.5px solid ${filterProp?T.ink:T.border}`,borderRadius:20,padding:'6px 28px 6px 12px',fontSize:12,color:filterProp?T.ink:T.ink,cursor:'pointer',fontWeight:filterProp?700:400,outline:'none',height:34 }}>
            <option value="">Alle Objekte</option>
            {properties.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <svg style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        {/* Status chip */}
        <div style={{ position:'relative' }}>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
            style={{ appearance:'none',WebkitAppearance:'none',background:filterStatus?'#f5f5f5':'white',border:`1.5px solid ${filterStatus?T.ink:T.border}`,borderRadius:20,padding:'6px 28px 6px 12px',fontSize:12,color:filterStatus?T.ink:T.ink,cursor:'pointer',fontWeight:filterStatus?700:400,outline:'none',height:34 }}>
            <option value="">Status</option>
            {Object.entries(STATUS_CFG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          <svg style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        {hasFilter && (
          <button onClick={() => { setSearch(''); setFilterProp(''); setFilterStatus(''); }} style={{ background:'none',border:'none',fontSize:13,color:T.ink,cursor:'pointer',fontWeight:600,padding:'0 4px' }}>
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ background:'white',border:`1px solid ${T.border}`,borderRadius:14,padding:'24px' }}>
          {[1,2,3].map(n=><div key={n} className="skeleton" style={{ height:56,borderRadius:8,marginBottom:12 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background:'white',border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden' }}>
          {hasFilter ? (
            <div style={{ textAlign:'center',padding:'60px 24px' }}>
              <div style={{ width:56,height:56,background:'#f7f7f7',borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
              <div style={{ fontSize:16,fontWeight:700,color:T.ink,marginBottom:8 }}>Keine Abrechnungen gefunden</div>
              <div style={{ fontSize:13,color:T.muted,lineHeight:1.7,marginBottom:20 }}>Versuche andere Filter oder setze die Suche zurück.</div>
              <button onClick={() => { setSearch(''); setFilterProp(''); setFilterStatus(''); }} className="btn-secondary">Filter zurücksetzen</button>
            </div>
          ) : (
            <div style={{ padding:'48px 40px' }}>
              <div style={{ display:'flex',alignItems:'flex-start',gap:32,marginBottom:40 }}>
                <div style={{ flexShrink:0,width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',border:'1.5px solid e5e5e5' }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:20,fontWeight:700,color:T.ink,marginBottom:8,letterSpacing:'-0.3px' }}>Noch keine Abrechnungen vorhanden</div>
                  <div style={{ fontSize:14,color:T.muted,lineHeight:1.8,maxWidth:520 }}>Erstelle Nebenkostenabrechnungen für deine Mieter auf Basis der erfassten Zählerstände und Betriebskosten.</div>
                  <button onClick={()=>setShowWizard(true)} className="btn-primary" style={{ marginTop:20 }}>+ Erste Abrechnung erstellen</button>
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',gap:16 }}>
                {[
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l2.5 2.5"/></svg>, title:'Zählerbasiert', text:'Verbräuche aus deinen Zählern fließen direkt in die Abrechnung ein.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>, title:'Pro Mieter', text:'Kosten werden anteilig auf alle Mietparteien verteilt.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, title:'PDF Export', text:'Fertige Abrechnungen als PDF exportieren und versenden.' },
                ].map((f,i)=>(
                  <div key={i} style={{ background:'#f7f7f7',borderRadius:12,padding:'18px 20px',border:`1px solid ${T.border}` }}>
                    <div style={{ width:36,height:36,background:'white',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12,border:`1px solid ${T.border}`,boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                    <div style={{ fontSize:13,fontWeight:700,color:T.ink,marginBottom:4 }}>{f.title}</div>
                    <div style={{ fontSize:12,color:T.muted,lineHeight:1.6 }}>{f.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        isMobile ? (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(a => {
              const sc = STATUS_CFG[a.status] || STATUS_CFG.entwurf;
              return (
                <div key={a.id} style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:T.ink }}>{a.title}</div>
                    <span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:sc.bg,color:sc.color,flexShrink:0 }}>{sc.label}</span>
                  </div>
                  <div style={{ fontSize:12, color:T.muted, marginBottom:4 }}>{propName(a.property_id)}</div>
                  <div style={{ fontSize:12, color:T.ink, marginBottom:12 }}>{fmtDate(a.periode_start)} – {fmtDate(a.periode_end)}</div>
                  <button style={{ width:'100%', background:'none',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 0',fontSize:12,cursor:'pointer',color:T.ink,fontFamily:T.font,fontWeight:600 }}>Öffnen</button>
                </div>
              );
            })}
          </div>
        ) : (
        <div style={{ background:'white',border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ display:'grid',gridTemplateColumns:'2fr 1.5fr 1.5fr 1fr 120px',padding:'11px 20px',borderBottom:`1px solid ${T.border}`,background:T.bg }}>
            {['Titel','Objekt','Zeitraum','Status',''].map(h=>(
              <span key={h} style={{ fontSize:11,fontWeight:700,letterSpacing:'0.5px',textTransform:'uppercase',color:'#9ca3af' }}>{h}</span>
            ))}
          </div>
          {filtered.map((a,i) => {
            const sc = STATUS_CFG[a.status] || STATUS_CFG.entwurf;
            return (
              <div key={a.id} style={{ display:'grid',gridTemplateColumns:'2fr 1.5fr 1.5fr 1fr 120px',padding:'14px 20px',alignItems:'center',borderBottom:i<filtered.length-1?`1px solid ${T.border}`:'none',transition:'background 0.1s' }}
                onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{ fontSize:13,fontWeight:700,color:T.ink }}>{a.title}</div>
                <div style={{ fontSize:12,color:T.muted }}>{propName(a.property_id)}</div>
                <div style={{ fontSize:12,color:T.ink }}>{fmtDate(a.periode_start)} – {fmtDate(a.periode_end)}</div>
                <div><span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:sc.bg,color:sc.color }}>{sc.label}</span></div>
                <div style={{ display:'flex',justifyContent:'flex-end' }}>
                  <button style={{ background:'none',border:`1px solid ${T.border}`,borderRadius:7,padding:'5px 12px',fontSize:11,cursor:'pointer',color:T.ink,fontFamily:T.font,fontWeight:600 }}>Öffnen</button>
                </div>
              </div>
            );
          })}
        </div>
        )
      )}
    </div>
  );
}
